import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Wallet, TrendingUp, TrendingDown, Plus, RotateCcw, Rocket, Loader2 } from 'lucide-react';
import { useCountUp } from '@/hooks/useCountUp';
import { useDemoAccountPositions, DemoPosition } from '@/hooks/useDemoAccountPositions';
import { useDemoAccountOrders, DemoOrder } from '@/hooks/useDemoAccountOrders';
import { formatDistanceToNow } from 'date-fns';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { motion, AnimatePresence } from 'framer-motion';

interface DemoAccount {
  id: string;
  account_name: string;
  balance: number;
  initial_balance: number;
  equity: number;
  margin_used: number;
  currency: string;
  created_at: string;
}

interface DemoAccountCardProps {
  account: DemoAccount;
  onReset: (id: string) => void;
  onAddFunds: (id: string) => void;
  onManageStrategies: (id: string) => void;
}

const formatPnl = (value: number): string => {
  const prefix = value >= 0 ? '+$' : '-$';
  return `${prefix}${Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const DemoAccountCard = ({
  account,
  onReset,
  onAddFunds,
  onManageStrategies,
}: DemoAccountCardProps) => {
  const balance = useCountUp({ end: account.balance, duration: 1500, prefix: '$', decimals: 2 });
  const initialBalance = Number(account.initial_balance);
  const currentBalance = Number(account.balance);
  const pnlAmount = currentBalance - initialBalance;
  const pnlPercentage = initialBalance > 0 ? (pnlAmount / initialBalance) * 100 : 0;
  const progressValue = Math.min((currentBalance / 100000) * 100, 100);

  const { positions, loading: posLoading } = useDemoAccountPositions(account.id);
  const { orders, loading: ordLoading } = useDemoAccountOrders(account.id);

  const sparklineData = orders
    .slice()
    .reverse()
    .map((o, i) => ({ x: i, y: o.price ?? 0 }));

  return (
    <Card className="relative overflow-hidden hover:shadow-lg transition-shadow bg-card/60 border-border/30">
      <div className="p-4 pb-3 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wallet className="h-4 w-4 text-primary/70" />
            <span className="font-semibold text-sm tracking-tight">{account.account_name}</span>
          </div>
          <Badge variant="default" className="text-[10px] h-5 capitalize">Active</Badge>
        </div>

        <div className="flex items-end justify-between">
          <div>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground/50 block">Balance</span>
            <span className="text-2xl font-bold tracking-tight font-mono-data">{balance}</span>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1 justify-end">
              {pnlAmount >= 0 ? (
                <TrendingUp className="h-3 w-3 text-[hsl(var(--success)/0.8)]" />
              ) : (
                <TrendingDown className="h-3 w-3 text-destructive/80" />
              )}
              <span className={`text-sm font-semibold font-mono-data ${pnlAmount >= 0 ? 'text-[hsl(var(--success)/0.85)]' : 'text-destructive/85'}`}>
                {formatPnl(pnlAmount)} ({pnlPercentage >= 0 ? '+' : ''}{pnlPercentage.toFixed(2)}%)
              </span>
            </div>
            <span className="text-[9px] text-muted-foreground/40">since inception</span>
          </div>
        </div>

        <Progress value={progressValue} className="h-1.5" />
      </div>

      <Tabs defaultValue="positions" className="px-4">
        <TabsList className="w-full h-8 bg-muted/30">
          <TabsTrigger value="positions" className="text-[11px] h-6 flex-1">
            Positions {positions.length > 0 && <Badge variant="secondary" className="ml-1 h-4 text-[9px] px-1">{positions.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="orders" className="text-[11px] h-6 flex-1">
            Orders {orders.length > 0 && <Badge variant="secondary" className="ml-1 h-4 text-[9px] px-1">{orders.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="performance" className="text-[11px] h-6 flex-1">Performance</TabsTrigger>
        </TabsList>

        <TabsContent value="positions" className="mt-2 max-h-[200px] overflow-y-auto space-y-1.5 pb-2">
          {posLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground/50" />
            </div>
          ) : positions.length > 0 ? (
            <AnimatePresence>
              {positions.map((pos) => (
                <PositionRow key={pos.id} position={pos} />
              ))}
            </AnimatePresence>
          ) : (
            <div className="flex items-center gap-2 py-6 justify-center">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary/40 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary/60" />
              </span>
              <span className="text-xs text-muted-foreground/50">Agent scanning market...</span>
            </div>
          )}
        </TabsContent>

        <TabsContent value="orders" className="mt-2 max-h-[200px] overflow-y-auto space-y-1 pb-2">
          {ordLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground/50" />
            </div>
          ) : orders.length > 0 ? (
            <AnimatePresence>
              {orders.map((order, i) => (
                <OrderRow key={order.id} order={order} isNew={i === 0} />
              ))}
            </AnimatePresence>
          ) : (
            <div className="text-center py-6 text-xs text-muted-foreground/50">No orders yet</div>
          )}
        </TabsContent>

        <TabsContent value="performance" className="mt-2 pb-3 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <MiniStat label="Positions" value={String(positions.length)} />
            <MiniStat label="Orders" value={String(orders.length)} />
            <MiniStat label="Margin Used" value={`$${account.margin_used.toFixed(0)}`} />
            <MiniStat label="Equity" value={`$${account.equity.toFixed(0)}`} />
          </div>
          {sparklineData.length > 2 && (
            <div className="h-[60px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={sparklineData}>
                  <Line type="monotone" dataKey="y" stroke="hsl(var(--primary))" strokeWidth={1.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <div className="flex flex-wrap gap-1.5 p-4 pt-2 border-t border-border/10">
        <Button size="sm" variant="outline" onClick={() => onManageStrategies(account.id)} className="text-xs h-7 gap-1">
          <Rocket className="h-3 w-3" />
          Deploy
        </Button>
        <Button size="sm" variant="outline" onClick={() => onAddFunds(account.id)} className="text-xs h-7 gap-1">
          <Plus className="h-3 w-3" />
          Funds
        </Button>
        <Button size="sm" variant="secondary" onClick={() => onReset(account.id)} className="text-xs h-7 gap-1 ml-auto">
          <RotateCcw className="h-3 w-3" />
          Reset
        </Button>
      </div>
    </Card>
  );
};

/* ─── Sub-components ─── */

const PositionRow = ({ position }: { position: DemoPosition }) => {
  const currentPrice = position.currentPrice ?? position.average_entry_price;
  const priceDiff = position.side === 'buy'
    ? (currentPrice - position.average_entry_price) * position.quantity
    : (position.average_entry_price - currentPrice) * position.quantity;
  const pnl = priceDiff || position.unrealized_pnl;
  const pnlPct = position.average_entry_price > 0 ? (pnl / (position.average_entry_price * position.quantity)) * 100 : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="rounded-md bg-muted/20 border border-border/10 p-2 text-xs"
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          <Badge variant={position.side === 'buy' ? 'default' : 'destructive'} className="text-[9px] h-4 px-1.5 uppercase">
            {position.side === 'buy' ? 'Long' : 'Short'}
          </Badge>
          <span className="font-medium">{position.symbol}</span>
          <span className="text-muted-foreground/50">{position.quantity} lots</span>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground/60">
          Entry: {position.average_entry_price.toFixed(5)} → {currentPrice.toFixed(5)}
        </span>
        <span className={`font-semibold font-mono-data ${pnl >= 0 ? 'text-[hsl(var(--success)/0.85)]' : 'text-destructive/85'}`}>
          {formatPnl(pnl)} ({pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(2)}%)
        </span>
      </div>
    </motion.div>
  );
};

const OrderRow = ({ order, isNew }: { order: DemoOrder; isNew: boolean }) => {
  const timeAgo = formatDistanceToNow(new Date(order.created_at), { addSuffix: true });

  return (
    <motion.div
      initial={isNew ? { opacity: 0, x: -10 } : { opacity: 1 }}
      animate={{ opacity: 1, x: 0 }}
      className="flex items-center justify-between rounded-md bg-muted/20 border border-border/10 px-2 py-1.5 text-xs"
    >
      <div className="flex items-center gap-1.5">
        <span className="text-muted-foreground/40 w-[60px] truncate">{timeAgo}</span>
        <Badge variant={order.side === 'buy' ? 'default' : 'destructive'} className="text-[9px] h-4 px-1.5 uppercase">
          {order.side}
        </Badge>
        <span className="font-medium">{order.symbol}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground/50 font-mono-data">
          {order.quantity} @ {order.price?.toFixed(5) ?? '—'}
        </span>
        <Badge variant="outline" className="text-[9px] h-4 px-1 capitalize">
          {order.status}
        </Badge>
      </div>
    </motion.div>
  );
};

const MiniStat = ({ label, value }: { label: string; value: string }) => (
  <div className="bg-muted/20 rounded-md p-2">
    <span className="text-[9px] uppercase tracking-wider text-muted-foreground/50 block">{label}</span>
    <span className="text-sm font-semibold font-mono-data">{value}</span>
  </div>
);